import type { ReactNode } from 'react';
import { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { toggleValueInArray } from 'app/utils/array';
import Checkbox from 'app/components/Checkbox';
import type { ButtonVariation } from 'app/components/Button';
import Dropdown, { ItemButton, ItemSeparator } from 'app/components/Dropdown';
import Loader from 'app/components/Loader';
import Input from 'app/components/Input';

import style from './style.scss';

interface SelectOption<T> {
  label: ReactNode;
  value: T;
  disabled?: boolean;
}

interface SelectAction {
  key: string;
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface Props<T extends number | string> {
  label?: ReactNode;
  forceLabel?: boolean;
  options: SelectOption<T>[];
  value: T | T[];
  onChange?: (value: T) => void;
  onMultiChange?: (value: T[]) => void;
  onClose?: () => void;
  multi?: boolean;
  clearLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  variation?: ButtonVariation;
  alignment?: Alignment;
  width?: number;
  className?: string;
  openedClassName?: string;
  portalClassName?: string;
  itemClassName?: string;
  error?: string;
  actions?: SelectAction[];
  withIcon?: boolean;
  withPortal?: boolean;
  withRadioButton?: boolean;
  initialFocus?: number;
  withSearch?: boolean;
}

const SelectDropdown = <T extends number | string>({
  label,
  options,
  value,
  variation,
  alignment,
  disabled = false,
  width = 200,
  className,
  openedClassName,
  actions,
  portalClassName,
  withIcon = true,
  forceLabel,
  loading = false,
  multi = false,
  clearLabel = '',
  itemClassName,
  onMultiChange,
  error,
  onClose,
  onChange,
  withPortal,
  withRadioButton = false,
  initialFocus = -1, // default from floating-ui doc
  withSearch = false
}: Props<T>) => {
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const selectedOption = options.find(option => option.value === value);
  const displayedLabel = selectedOption && !forceLabel ? selectedOption.label : label;

  const handleDropdownChange = (opened: boolean) => {
    setOpened(opened);
    if (opened) {
      setSearch('');
    }

    if (!opened && onClose) {
      onClose();
    }
  };

  const handleOptionClick = (newValue: T) => {
    if (multi) {
      if (onMultiChange) {
        const currentList = value instanceof Array ? value : [value];
        const newMultiValue = toggleValueInArray(currentList, newValue);
        onMultiChange(newMultiValue);
      }
    } else {
      if (onChange) {
        onChange(newValue);
      }
      setOpened(false);
    }
  };

  const clearValue = () => {
    if (onMultiChange) {
      onMultiChange([]);
    }
  };

  const handleActionClick = (handleClick: () => void) => {
    handleClick();
    setOpened(false);
  };

  const isOptionSelected = (option: SelectOption<T>): boolean => {
    if (value instanceof Array) {
      return value.some(item => item === option.value);
    }
    return value === option.value;
  };

  const renderOptions = () => {
    if (loading) {
      return (
        <div className={style.loaderContainer}>
          <Loader size={6} />
        </div>
      );
    }

    if (error) {
      return <div className={style.error}>{error}</div>;
    }

    return (
      <>
        {options
          .filter(option =>
            withSearch && option.label
              ? option.label.toString().toLowerCase().includes(search.toLowerCase())
              : true
          )
          .map(option => {
            const handleChange = () => handleOptionClick(option.value);
            const selected = isOptionSelected(option);
            const optionClassName = classnames(style.option, {
              [style.option_multi]: multi,
              [style.option_selected]: selected
            });
            // in multiple select, only checkbox is focusable
            const buttonTabIndex = multi ? -1 : undefined;
            return (
              <div key={option.value} className={optionClassName}>
                {multi && (
                  <Checkbox
                    status={selected ? 'checked' : 'unchecked'}
                    size="medium"
                    onChange={handleChange}
                  />
                )}
                {withRadioButton && (
                  <div
                    className={classnames(style.radioButton, {
                      [style.selectedRadioButton]: selected
                    })}
                  />
                )}
                <ItemButton
                  onClick={handleChange}
                  className={classnames(style.optionButton, itemClassName)}
                  tabIndex={buttonTabIndex}
                  disabled={option.disabled}
                >
                  {option.label}
                </ItemButton>
              </div>
            );
          })}
        {clearLabel && (
          <ItemButton onClick={clearValue} className={style.clearButton}>
            {clearLabel}
          </ItemButton>
        )}
      </>
    );
  };

  return (
    <Dropdown
      label={displayedLabel}
      variation={variation}
      alignment={alignment}
      disabled={disabled}
      opened={opened}
      onOpenChange={handleDropdownChange}
      width={width}
      className={className}
      openedClassName={openedClassName}
      portalClassName={portalClassName}
      withIcon={withIcon}
      withPortal={withPortal}
      initialFocus={initialFocus}
      changeFocusOnArrowDown={!multi}
    >
      <div>
        {withSearch && (
          <div className={style.searchContainer}>
            <Input
              value={search}
              type="text"
              placeholder={t('component-select_dropdown-search')}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        )}
        {renderOptions()}
        {actions && (
          <>
            {options.length > 0 && <ItemSeparator />}
            {actions.map(action => (
              <ItemButton
                key={action.key}
                onClick={() => handleActionClick(action.onClick)}
                disabled={action.disabled}
              >
                {action.label}
              </ItemButton>
            ))}
          </>
        )}
      </div>
    </Dropdown>
  );
};

SelectDropdown.displayName = 'SelectDropdown';

export default SelectDropdown;
export type { SelectOption, SelectAction };
